import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { IntlProvider } from 'react-intl'

import { IntlErrorCode, OnErrorFn } from '@formatjs/intl'

import { LyraIntl, LyraIntlState } from './LyraIntl'
import { LyraIntlContext, LyraIntlContextType } from './LyraIntlContext'

export type LyraIntlProps<LanguageTag extends string> = PropsWithChildren<{
  intl: LyraIntl<LanguageTag>
  countryIsoCode: string
  timeZone: string
  initialLanguage?: string | LanguageTag | null
  textComponent?: React.ComponentType
  initLocaleFallbackTimeoutMs?: number
}>

const onError: OnErrorFn = (error) => {
  if (error.code === IntlErrorCode.MISSING_TRANSLATION) {
    return
  }
  console.error(error)
}

export function LyraIntlProvider<LanguageTag extends string>({
  intl,
  countryIsoCode,
  timeZone,
  initialLanguage = null,
  textComponent,
  initLocaleFallbackTimeoutMs,
  children,
}: LyraIntlProps<LanguageTag>) {
  const [intlState, setIntlState] = useState<LyraIntlState<LanguageTag> | null>(null)

  useEffect(() => {
    let cancelled = false

    // Hard timeout (opt-in via initLocaleFallbackTimeoutMs): on iOS 26.4.2,
    // native calls in initLocale (e.g. Localization.getLocalizationAsync,
    // locale-polyfill loading) can hang post-Updates.reloadAsync, leaving this
    // provider returning null forever and the whole app tree unmounted.
    // Falling back to a minimal default-language state lets the app mount;
    // <FormattedMessage> entries will use their defaultMessage from code.
    //
    // Note: the fallback uses intl.config.defaultLanguage directly rather than
    // running through computeLocale(), so the resulting locale string may
    // differ from a successful init (e.g. "en-US" vs "en-Latn-US") and won't
    // apply countryIsoCode overrides. Accepted tradeoff to keep this provider
    // free of computeLocale's dependencies.
    //
    // Only the mobile app should opt in — web and provider portal don't hit
    // the iOS hang and a silent fallback would mask real init failures there.
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | undefined
    if (initLocaleFallbackTimeoutMs != null) {
      fallbackTimeoutId = setTimeout(() => {
        if (cancelled) return
        const defaultLocale = intl.config.defaultLanguage
        setIntlState(
          (prev) =>
            prev ?? {
              language: defaultLocale,
              reactIntlConfig: { locale: defaultLocale, defaultLocale, messages: {} },
              disabledTranslationsReactIntlConfig: { locale: defaultLocale, defaultLocale, messages: {} },
            },
        )
      }, initLocaleFallbackTimeoutMs)
    }

    intl
      .initLocale(initialLanguage, countryIsoCode)
      .then((newIntlState) => {
        if (cancelled) return
        clearTimeout(fallbackTimeoutId)
        setIntlState(newIntlState)
      })
      .catch((error) => {
        console.error('LyraIntlProvider: initLocale failed, falling back to defaults', error)
      })

    return () => {
      cancelled = true
      clearTimeout(fallbackTimeoutId)
    }
  }, [intl, initialLanguage, countryIsoCode])

  const contextValue = useMemo(
    (): LyraIntlContextType | null =>
      intlState
        ? {
            availableLanguages: intl.config.languages,
            activeLanguage: intlState.language,
            resolved: intlState.resolved,
            // Provide the full config including messages for <EnableTranslations>
            intlConfig: {
              ...intlState.reactIntlConfig,
              timeZone,
              onError,
              textComponent,
            },
            disabledTranslationsIntlConfig: {
              ...intlState.disabledTranslationsReactIntlConfig,
              timeZone,
              onError,
              textComponent,
            },
            setLanguage: async (lang) => {
              const newIntlState = await intl.changeLanguage(lang as LanguageTag, countryIsoCode)
              setIntlState(newIntlState)
            },
          }
        : null,
    [countryIsoCode, intl, intlState, timeZone, textComponent],
  )

  if (contextValue == null) {
    return null
  }

  return (
    <LyraIntlContext.Provider value={contextValue}>
      <IntlProvider {...contextValue.disabledTranslationsIntlConfig}>{children}</IntlProvider>
    </LyraIntlContext.Provider>
  )
}
